<template>
    <breadcrumbs
        v-if="!selectedFilters.is_renewal_site"
        :breadcrumbs="breadcrumbs"
    />

    <provider-list
        v-if="!selectedFilters.is_renewal_site"
        :provider-list="providerList"
        :selected-provider="provider"
        :selected-filters="selectedFilters"
    />

    <provider-list-renewal
        v-if="selectedFilters.is_renewal_site"
        :provider-list="providerList"
        :selected-provider="provider"
        :selected-filters="selectedFilters"
    />

    <div :class="[
        'filters',
        providerName ? `content-${providerName}` : `content-default`,
        providerName ? `background-gradient-${providerName}` : `background-gradient-default`
    ]">
        <filter-list
            :filters="{...filters, familyOptions}"
            :provider-name="providerName"
            :devices="devices"
            :family="family"
            :carousel-banners="carouselBanners"
            :selected-filters="selectedFilters"
            @filter="getOffers"
            @filterChange="onChangeFilters($event)"
            @generate-breadcrumbs="generateBreadcrumbs"
            @futureModel="getFutureModel"
        />
    </div>

    <div class="offer-list" ref="scrollComponent">
        <div v-for="(chunk, rowIndex) in offerChunks"
             v-if="!(loading && firstLoad)"
             :key="rowIndex"
             class="row"
        >
            <div v-for="(offer, columnIndex) in chunk"
                 :key="columnIndex"
                 class="col-12 col-sm-12 col-md-6 col-xxl-3"
            >
                <offer :offer="offer"/>
            </div>

            <div v-if="subscriptionDeviceOffers.length && selectedFilters.model && rowIndex === offerChunks.length - 1"
                 class="col-12 col-sm-12 col-md-6 col-xxl-3"
            >
                <subscription-device-offer
                    :offers="subscriptionDeviceOffers"
                ></subscription-device-offer>
            </div>

            <div class="col-12">
                <ilt-table-chunk
                    v-if="!isMobile && selectedFilters.has_device && !selectedFilters.is_business"
                    :chunk="chunk"
                    :row-index="rowIndex"
                />
            </div>
        </div>

        <loader :loading="loading"/>

        <div class="no-results" v-if="!offers.length && !firstLoad && !showFutureModelContent">
            Er zijn geen aanbiedingen gevonden voor de huidige combinatie in de filter.
            <a href="/">Klik hier om de filter te resetten</a>
        </div>
        <div v-else-if="showFutureModelContent" class="row">
            <div class="col-sm-6" v-html="selectedModel.content"></div>

            <div class="col-sm-6">
                <form
                    method="post"
                    class="needs-validation form"
                    id="future-models-form"
                    novalidate
                    @submit.prevent="onSubmit"
                >
                    <div class="alert alert-success" v-if="showSuccessAlert">
                        Bedankt, wij houden je op de hoogte!
                    </div>

                    <div class="form-group">
                        <input
                            id="phone-number" type="tel"
                            class="form-control" placeholder="Telefoonnummer"
                            :class="{'is-invalid': errors.phone}"
                            v-model="customer.phone" required
                        >
                        <div v-if="errors.phone" class="invalid-feedback">
                            {{ errors.phone[0] }}
                        </div>
                    </div>

                    <div class="form-group">
                        <input
                            id="email" type="email"
                            class="form-control" placeholder="E-mail"
                            :class="{'is-invalid': errors.email}"
                            v-model="customer.email" required
                        >
                        <div v-if="errors.email" class="invalid-feedback">
                            {{ errors.email[0] }}
                        </div>
                    </div>
                    <div class="form-row mt-3 mb-3">
                        <div class="col text-right submit-btn mt-3">
                            <label class="bold text-left"></label>
                            <button class="btn btn-warning bold" type="submit">
                                Houd me op de hoogte!
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <renewal-check-modal
        :provider-list="providerList"
        :is-business="selectedFilters.is_business ? 1 : 0"
    />

    <save-offer-modal
    />
</template>

<script>
import {PER_PAGE, SORT_TYPE} from "../constants";
import InfiniteLoading from 'v3-infinite-loading';

import Breadcrumbs from '../components/Breadcrumbs';
import Offer from '../components/Offer';
import Loader from '../components/Loader';
import FilterList from '../components/FilterList';
import ProviderList from '../components/ProviderList';
import IltTableChunk from '../components/IltTableChunk';
import SubscriptionDeviceOffer from "../components/SubscriptionDeviceOffer";
import RenewalCheckModal from "../components/modals/RenewalCheckModal";
import ProviderListRenewal from "../components/ProviderListRenewal";
import SaveOfferModal from "../components/modals/SaveOfferModal";

export default {
    components: {
        ProviderListRenewal,
        SubscriptionDeviceOffer,
        RenewalCheckModal,
        IltTableChunk,
        ProviderList,
        FilterList,
        Breadcrumbs,
        Loader,
        Offer,
        InfiniteLoading,
        SaveOfferModal
    },

    props: {
        providerName: {
            type: String,
            default: ''
        },

        providerList: {
            type: Array,
            required: true
        },

        carouselBanners: {
            type: Array,
            default: []
        },

        devices: {
            type: Object,
            default: {}
        },

        preFilters: {
            type: Object,
            default: {}
        },
    },

    data() {
        return {
            loading: false,
            offers: [],
            filters: {},
            breadcrumbs: [],
            page: 1,
            firstLoad: true,
            hasMoreResults: true,
            selectedFilters: {},
            subscriptionDeviceOffers: [],
            showFutureModelContent: false,
            showSuccessAlert: false,
            selectedModel: null,
            customer: {},
            errors: [],
        }
    },

    computed: {
        offerChunks() {
            let chunks = [];

            for (let i = 0; i < this.offers.length; i += 4) {
                chunks.push(this.offers.slice(i, i + 4))
            }

            return chunks;
        },

        provider() {
            return this.providerList.find(el => el.name.toLowerCase() === this.providerName)
        },

        family() {
            if (!this.provider) {
                return null
            }

            return this.provider.families.find(el => el.id === this.selectedFilters.family_id)
        },

        familyOptions() {
            if (!this.provider) {
                return null
            }

            return this.provider.families
                .sort((a, b) => a.sort_order - b.sort_order)
                .map(el => {
                    return {
                        value: el.id,
                        label: el.name_website ?? el.name
                    }
                });
        }
    },

    created() {
        this.selectedFilters = {
            has_topdeals: !this.provider,
            provider_id: this.provider ? this.provider.id : null,
            family_id: this.preFilters.family ? this.preFilters.family : (this.familyOptions ? this.familyOptions[0].value : null),
            is_business: this.preFilters.is_business ? this.preFilters.is_business : false,
            is_renewal: this.preFilters.is_renewal ? this.preFilters.is_renewal : false,
            has_device: this.preFilters.has_device,
            internet_speed: this.preFilters.connection_speed ?? null,
            duration: this.preFilters.duration ?? [],
            bundle: this.preFilters.minutes ?? [],
            internet: this.preFilters.data ?? [],
            brand: this.preFilters.brand ?? '',
            model: this.preFilters.model ?? '',
            discount: this.preFilters.discount ?? '',
            is_renewal_site: this.preFilters.is_renewal_site ?? false,
            per_page: 12
        }

        this.loadData().then(() => {
            this.generateBreadcrumbs()
            this.loading = false;
        })
    },

    mounted() {
        window.addEventListener("scroll", this.handleScroll)
    },

    unmounted() {
        window.removeEventListener("scroll", this.handleScroll)
    },

    methods: {
        async loadData() {
            await this.getSubscriptionTypeOptions()

            if (!this.selectedFilters.has_topdeals) {
                await Promise.all([
                    this.getCustomerDiscountOptions(),
                    this.getBundleOptions(),
                    this.getInternetOptions(),
                    this.getDurationOptions(),
                    this.getInternetSpeedOptions()
                ])
            }

            await this.getOffers()
        },

        handleScroll() {
            if (!this.hasMoreResults || this.loading || this.firstLoad) {
                return;
            }

            if (window.scrollY + window.innerWidth >= document.body.scrollHeight - 150) {
                this.getMoreOffers()
            }
        },

        async getOffers() {
            this.data = [];
            this.loading = true
            this.page = 1;
            this.firstLoad = true;
            this.hasMoreResults = true;
            await axios
                .get('/api/subscription-offers', {params: {
                        ...this.selectedFilters,
                        page: this.page
                    }})
                .then(({data}) => {
                    if (data.length < PER_PAGE) {
                        this.hasMoreResults = false
                    } else {
                        this.page++
                    }

                    this.firstLoad = false;
                    this.loading = false;
                    this.offers = data

                    if (this.selectedFilters.model
                        && this.selectedFilters.brand
                        && this.selectedFilters.has_topdeals
                        && !this.selectedFilters.is_business
                    ) {
                        this.getSubscriptionDeviceOffers()
                    }
                })
        },

        async getMoreOffers() {
            this.loading = true;

            await axios
                .get('/api/subscription-offers', {params: {
                    ...this.selectedFilters,
                    page: this.page
                }})
                .then(({data}) => {
                    if (data.length < PER_PAGE) {
                        this.hasMoreResults = false
                    } else {
                        this.page++
                    }

                    this.loading = false
                    this.offers = [
                        ...this.offers,
                        ...data
                    ]
                })
        },

        async getSubscriptionTypeOptions() {
            await axios
                .get('/api/subscription-offers/filters/type-options', {params: this.selectedFilters})
                .then(({data}) => {
                    this.filters.typeOptions = data
                })
        },

        async getCustomerDiscountOptions() {
            await axios
                .get('/api/subscription-offers/filters/discount-options', {params: this.selectedFilters})
                .then(({data}) => {
                    this.filters.discountOptions = data

                    this.selectedFilters.discount = data.map(el => el.value).includes(this.selectedFilters.discount) ? this.selectedFilters.discount : ''
                })
        },

        async getBundleOptions() {
            await axios
                .get('/api/subscription-offers/filters/bundle-options', {params: this.selectedFilters})
                .then(({data}) => {
                    this.filters.bundleOptions = data
                    let selectedValues = []

                    data.forEach(el => {
                        if (this.selectedFilters.bundle.includes(el.value)) {
                            selectedValues.push(el.value)
                        } else if (el.alternative && this.selectedFilters.bundle.includes(el.alternative) && this.preFilters.discount && this.preFilters.discount !== this.selectedFilters.discount) {
                            selectedValues.push(el.value)
                        }
                    })

                    this.selectedFilters.bundle = selectedValues
                })
        },

        async getInternetOptions() {
            await axios
                .get('/api/subscription-offers/filters/internet-options', {params: this.selectedFilters})
                .then(({data}) => {
                    this.filters.internetOptions = data
                    let selectedValues = []

                    data.forEach(el => {
                        if (this.selectedFilters.internet.includes(el.value)) {
                            selectedValues.push(el.value)
                        } else if (el.alternative && this.selectedFilters.internet.includes(el.alternative) && this.preFilters.discount && this.preFilters.discount !== this.selectedFilters.discount) {
                            selectedValues.push(el.value)
                        }
                    })

                    this.selectedFilters.internet = selectedValues
                })
        },

        async getDurationOptions() {
            await axios
                .get('/api/subscription-offers/filters/duration-options', {params: this.selectedFilters})
                .then(({data}) => {
                    this.filters.durationOptions = data
                    let selectedValues = []

                    data.forEach(el => {
                        if (this.selectedFilters.duration.includes(el.value)) {
                            selectedValues.push(el.value)
                        }
                    })

                    this.selectedFilters.duration = selectedValues
                })
        },

        async getInternetSpeedOptions() {
            await axios
                .get('/api/subscription-offers/filters/speed-options', {params: this.selectedFilters})
                .then(({data}) => {
                    this.filters.speedOptions = data

                    const values = data.map(el => el.value);

                    if (values.length === 1) {
                        this.selectedFilters.speed = values[0]
                    } else {
                        this.selectedFilters.speed = values.includes(this.selectedFilters.speed) ? this.selectedFilters.speed : null
                    }
                })
        },

        async generateBreadcrumbs() {
            this.breadcrumbs = [{
                label: 'Home',
                link: '/'
            }];

            let breadCrumbLabels = ['Home'];

            if (this.selectedFilters.is_business) {
                breadCrumbLabels.push(['Zakelijk']);

                this.breadcrumbs.push({
                    label: 'Zakelijk',
                    link: this.generateBreadcrumbLink(breadCrumbLabels)
                });
            }

            if (this.providerName) {
                breadCrumbLabels.push(this.$filters.capitalizeFirstLetter(this.providerName));

                this.breadcrumbs.push({
                    label: this.$filters.capitalizeFirstLetter(this.providerName),
                    link: this.generateBreadcrumbLink(breadCrumbLabels)
                });
            }

            if (!this.selectedFilters.has_device) {
                breadCrumbLabels.push(this.$filters.capitalizeFirstLetter(SORT_TYPE.SIM_ONLY));

                this.breadcrumbs.push({
                    label: this.$filters.capitalizeFirstLetter(SORT_TYPE.SIM_ONLY),
                    link: this.generateBreadcrumbLink(breadCrumbLabels)
                });

                return;
            }

            if (this.selectedFilters.brand) {
                breadCrumbLabels.push(this.selectedFilters.brand);

                this.breadcrumbs.push({
                    label: this.selectedFilters.brand,
                    link: this.generateBreadcrumbLink(breadCrumbLabels)
                });
            }

            if (this.selectedFilters.model) {
                breadCrumbLabels.push(this.selectedFilters.model);

                this.breadcrumbs.push({
                    label: this.selectedFilters.model,
                    link: this.generateBreadcrumbLink(breadCrumbLabels)
                });
            }
        },

        generateBreadcrumbLink(breadCrumbLabels) {
            const filter = this.$filters;
            let links = breadCrumbLabels.map(function (el) {
                if (el === 'Home') {
                    return '';
                }
                return filter.lowerCase(el, '-');
            })
            links = links.join('/');
            return links;
        },

        async onChangeFilters(type) {
            if (this.selectedFilters.has_topdeals) {
                await this.getOffers()

                return
            }

            if (['internet', 'bundle'].includes(type)) {
                await this.getCustomerDiscountOptions()
                await this.getOffers()

                return;
            }

            if (['is_renewal', 'family'].includes(type)) {
                await this.getCustomerDiscountOptions()
            }

            await Promise.all([
                this.getBundleOptions(),
                this.getInternetOptions(),
                this.getDurationOptions(),
                this.getInternetSpeedOptions()
            ]);

            await this.getOffers()
        },

        async getSubscriptionDeviceOffers() {
            await axios
                .get('/api/subscription-offers/device-offers/search', {params: this.selectedFilters})
                .then(({data}) => {
                    this.subscriptionDeviceOffers = data
                })
        },

        async getFutureModel (selectedModel) {
            this.selectedModel = selectedModel;
            this.showFutureModelContent = true;
        },

        async onSubmit() {
            this.errors = {};
            let data = {
                ...this.customer,
                brand: this.selectedModel.brand,
                model: this.selectedModel.model,
            }
            data = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== '' && v !== null))

            let formData = new FormData();

            Object.keys(data).forEach((key) => {
                formData.append(key, data[key]);
            });

            await axios
                .post(`/api/future-model/create`, formData)
                .then(({data}) => {
                    this.showSuccessAlert = true;
                })
                .catch((error) => {
                    data = error.response.data;
                    this.showSuccessAlert = false;
                    if (data.errors) {
                        this.errors = data.errors
                    } else {
                        this.errorMessage = data.error
                    }
                })
        }
    }
}
</script>
